import { Link, NavLink } from "react-router-dom";
import { Bookmark, LayoutDashboard, LogOut, Moon, Search, ShoppingBag, Sun, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { useBorrowerTheme } from "@/lib/borrowerTheme";

const navItems = (isAdmin) => [
  { to: "/", label: "Pilih Jadwal", icon: Ticket, id: "nav-start" },
  { to: "/katalog", label: "Katalog", icon: Search, id: "nav-catalog" },
  { to: "/paket", label: "Paket", icon: Bookmark, id: "nav-templates" },
  { to: "/peminjaman", label: "Booking", icon: Ticket, id: "nav-bookings" },
  ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: LayoutDashboard, id: "nav-admin" }] : []),
];

export default function Layout({ children }) {
  const { theme, toggleTheme } = useBorrowerTheme();
  const { user, logout } = useAuth();
  const { totalQty } = useCart();
  const items = navItems(user?.role === "admin");

  return (
    <div className="borrower-light min-h-screen bg-[#C2D6EB] text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
          <Link to="/" data-testid="brand-logo" className="flex min-w-0 shrink items-center gap-1 sm:gap-3">
            <img src="/yn-logo.svg" alt="YukNgaji" className="brand-yn h-8 w-8 shrink-0 object-contain sm:h-9 sm:w-9" />
            <span className="font-heading text-base font-bold sm:text-lg tracking-tight text-slate-900">Dexter</span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === "/"}
                data-testid={i.id}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-[#e7f1fc] text-[#004f9e]" : "text-slate-500 hover:text-slate-900"
                  }`
                }
              >
                {i.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-0 sm:gap-3">
            <Link to="/checkout" data-testid="cart-button"
              className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-lg border border-[#0067c8] bg-[#0067c8] px-2 text-sm sm:gap-2 sm:px-3 font-semibold text-white transition-colors duration-200 hover:border-[#004f9e] hover:bg-[#004f9e]">
              <ShoppingBag className="h-5 w-5" />
              <span data-testid="cart-badge" className="rounded-full bg-white/25 px-2 py-0.5 text-xs font-bold tabular-nums">
                {totalQty}
              </span>
              <span className="sr-only">Keranjang, {totalQty} unit</span>
            </Link>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Tema gelap"
              aria-pressed={theme === "dark"}
              title={theme === "dark" ? "Ganti ke tema terang" : "Ganti ke tema gelap"}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0067c8]"
            >
              {theme === "dark" ? <Moon className="h-5 w-5" aria-hidden="true" /> : <Sun className="h-5 w-5" aria-hidden="true" />}
            </button>
            <span data-testid="current-user-name" className="hidden text-sm text-slate-500 sm:block">
              {user?.name}
            </span>
            <Button
              data-testid="logout-button"
              aria-label="Keluar"
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px] rounded-full text-slate-500 hover:text-slate-900"
              onClick={async () => {
                await logout();
                window.location.replace("/login");
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 pt-8 [padding-bottom:calc(7rem+env(safe-area-inset-bottom,0px))] md:[padding-bottom:4rem]">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 [padding-bottom:env(safe-area-inset-bottom,0px)] backdrop-blur-xl md:hidden">
        <div className="flex">
          {items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === "/"}
              data-testid={`${i.id}-mobile`}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium ${
                  isActive ? "text-[#0067c8]" : "text-slate-500"
                }`
              }
            >
              <i.icon className="h-5 w-5" />
              {i.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
